import type { ClubMessage } from "@mixitone/models";
import { clubMessageHtmlToPlainText } from "@mixitone/util";

type ModerationStatus = "draft" | "pending_ai" | "approved_directory" | "rejected_directory";

/** Dispatched after a club message is saved so club home can reload `club_messages`. */
export const CLUB_MESSAGES_UPDATED_EVENT = "mixitone:club-messages-updated";

export function notifyClubMessagesUpdated(clubId: string): void {
  window.dispatchEvent(new CustomEvent(CLUB_MESSAGES_UPDATED_EVENT, { detail: { clubId } }));
}

/** Single-line preview for list rows (whitespace collapsed, truncated). */
export function previewClubMessageBody(body: string | undefined | null, maxLen = 72): string {
  const plain = clubMessageHtmlToPlainText(body ?? "");
  if (!plain) return "Empty message";
  const oneLine = plain.replace(/\s+/g, " ");
  if (oneLine.length <= maxLen) return oneLine;
  return `${oneLine.slice(0, maxLen)}…`;
}

export function formatClubMessageStatusLabel(message: ClubMessage): string {
  if (message.is_archived) return "Archived";
  const s = message.directory_moderation_status;
  switch (s) {
    case "draft":
      return "Draft";
    case "approved_directory":
      return "On directory";
    case "pending_ai":
      return "Updating";
    case "rejected_directory":
      return "Not listed";
    default:
      return "";
  }
}

export function computeDirectoryStatus(
  previous: ClubMessage | undefined,
  input: {
    body: string;
    public_in_directory: boolean;
    is_archived: boolean;
  },
): ModerationStatus {
  if (input.is_archived) {
    return (previous?.directory_moderation_status ?? "draft") as ModerationStatus;
  }
  if (!input.public_in_directory) {
    return "draft";
  }
  if (!previous?.uniqueId) {
    return "pending_ai";
  }
  const prevStatus = previous.directory_moderation_status;
  const contentChanged = previous.body !== input.body;
  if (prevStatus === "approved_directory" && contentChanged) {
    return "pending_ai";
  }
  if (prevStatus === "draft" || prevStatus === "rejected_directory") {
    return "pending_ai";
  }
  if (prevStatus === "pending_ai") {
    return "pending_ai";
  }
  if (prevStatus === "approved_directory" && !contentChanged) {
    return "approved_directory";
  }
  return "pending_ai";
}
